/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField,
  Button, CircularProgress,
} from '@mui/material';
import Swal from 'sweetalert2';
import { api } from '../../../../api';
import { formKeyHandler } from 'utils/formKeyboard';

const TEAL = '#00798E';
const UPPER = new Set(['name', 'short_name', 'address', 'contact_person', 'gst_number', 'drug_license_number']);
const upperSx = { '& .MuiInputBase-input': { textTransform: 'uppercase' } };

const EMPTY = {
  name: '', short_name: '', gst_number: '', drug_license_number: '',
  contact_person: '', phone: '', email: '', address: '',
};

/**
 * Quick-add a Supplier from the GRN screen.
 *   props: open, initialName, onClose(), onCreated(supplier)
 */
export default function SupplierQuickAdd({ open, initialName = '', onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...EMPTY, name: (initialName || '').toUpperCase() });
  }, [open, initialName]);

  const set = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: UPPER.has(k) ? e.target.value.toUpperCase() : e.target.value }));

  const save = async () => {
    if (!form.name.trim()) { Swal.fire({ icon: 'warning', title: 'Supplier name is required' }); return; }
    setSaving(true);
    try {
      const res = await api.pharmacy.createSupplier(form);
      const supplier = res?.data || res;
      Swal.fire({ icon: 'success', title: 'Supplier created', toast: true, position: 'top-end', showConfirmButton: false, timer: 1600 });
      onCreated(supplier);
      onClose();
    } catch { /* ApiClient shows the error */ }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ color: TEAL, fontWeight: 700, fontSize: '1rem', borderBottom: '1px solid #f0f2f5', pb: 1.5 }}>
        New Supplier
      </DialogTitle>
      <DialogContent sx={{ pt: '18px !important' }} onKeyDown={formKeyHandler(save)}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={8}>
            <TextField autoFocus fullWidth size="small" label="Supplier Name *" value={form.name} onChange={set('name')} sx={upperSx} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Short Name" value={form.short_name} onChange={set('short_name')} sx={upperSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="GST Number" value={form.gst_number} onChange={set('gst_number')} sx={upperSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Drug License Number" value={form.drug_license_number} onChange={set('drug_license_number')} sx={upperSx} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Contact Person" value={form.contact_person} onChange={set('contact_person')} sx={upperSx} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="Phone" value={form.phone} onChange={set('phone')} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="Email" value={form.email} onChange={set('email')} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Address" value={form.address} onChange={set('address')} multiline rows={2} sx={upperSx} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: '#7b809a' }}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={saving}
          sx={{ textTransform: 'none', bgcolor: TEAL, color: 'white !important', fontWeight: 600, minWidth: 130, '&:hover': { bgcolor: '#016476' } }}>
          {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Save (Ctrl+↵)'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
